/*
 * URL construction for the Docs link.
 *
 * Kept free of React and of Backstage's catalog client so it can be tested
 * directly - the same reason graphHref lives in its own file.
 *
 * The link's whole job is to skip TechDocs' index page when that index would be
 * a one-row table. So the rule is: exactly one documented entity -> land in its
 * reader; anything else -> the /docs index, which earns its place for zero
 * (nothing to shortcut to) or several (a genuine list).
 */

/**
 * The minimum of a catalog entity docsHref reads. Matches the `fields` the nav
 * item asks getEntities for, so a real Entity is assignable to it.
 */
export interface DocumentedEntity {
  kind: string;
  metadata: { name: string; namespace?: string };
}

/** TechDocs' index. Also the fallback landing whenever a shortcut is not warranted. */
export const DOCS_INDEX = '/docs';

/**
 * Where "Docs" should point given the entities that carry a techdocs-ref: the
 * single one's reader when there is exactly one, otherwise the index.
 */
export function docsHref(entities: DocumentedEntity[]): string {
  if (entities.length !== 1) {
    // Zero: nothing to shortcut to. Several: a list is worth showing.
    return DOCS_INDEX;
  }
  const [entity] = entities;
  // The reader route is /docs/:namespace/:kind/:name, matched lowercased - a
  // capitalized kind 404s inside the doc site.
  const namespace = (entity.metadata.namespace ?? 'default').toLocaleLowerCase('en-US');
  const kind = entity.kind.toLocaleLowerCase('en-US');
  return `${DOCS_INDEX}/${namespace}/${kind}/${entity.metadata.name}`;
}
